/**
 * 병원 페이지 구조화된 데이터 생성 시스템
 * JSON 템플릿 기반으로 읽기 쉽고 유지보수하기 쉬운 구조
 */

import { WithContext } from 'schema-dts'

import { Locales, localeToCountryUrlPrefix } from '@/utils/language'

import type { HospitalView } from '@/features/hospital/types/hospital.type'
import type { DoctorForUser } from '@/services/doctors/doctor.types'
import type { ServiceOffer } from '@/features/service-offer/service-offer.type'

// Base URL for generating IDs
const BASE_URL = 'https://www.gangnamunni.com'

/**
 * Generate URLs with locale prefix
 */
function getUrl(path: string, locale: Locales): string {
  const prefix = localeToCountryUrlPrefix(locale)
  return `${BASE_URL}${prefix}${path}`
}

/**
 * 병원 스키마 생성 (LocalBusiness + MedicalBusiness)
 */
export function createHospitalSchema(
  hospital: HospitalView,
  locale: Locales,
  options?: {
    doctors?: DoctorForUser[]
    services?: ServiceOffer[]
  },
): WithContext<any> {
  const hospitalUrl = getUrl(`/hospitals/${hospital.hospitalId}`, locale)

  return {
    '@context': 'https://schema.org',
    '@type': ['LocalBusiness', 'MedicalBusiness'],
    '@id': hospitalUrl,
    name: hospital.name,
    url: hospitalUrl,
    image: hospital.images.mainImageUrl || hospital.images.profileImageUrl || '',
    logo: hospital.images.profileImageUrl || '',

    // 주소 정보 (Google 리치리절트 필수)
    address: {
      '@type': 'PostalAddress',
      streetAddress: hospital.location.address,
      addressLocality: hospital.location.sido || '',
      addressCountry: hospital.country || 'KR',
    },

    // 좌표 (로컬비즈니스 필수)
    geo: {
      '@type': 'GeoCoordinates',
      latitude: hospital.location.lat,
      longitude: hospital.location.lng,
    },

    // 평점 정보
    aggregateRating:
      hospital.statistic.rating && hospital.statistic.reviewCount
        ? {
            '@type': 'AggregateRating',
            ratingValue: hospital.statistic.rating,
            reviewCount: hospital.statistic.reviewCount,
            bestRating: 10,
          }
        : undefined,

    // 가격대
    priceRange: '$$',

    // 의료 전문분야
    medicalSpecialty: ['PlasticSurgery', 'Dermatology'],

    // 결제 방법
    paymentAccepted: ['Cash', 'Credit Card'],

    // 소셜미디어 링크
    sameAs: getSocialMediaUrls(locale),

    // 의료진 정보 (간단한 참조)
    employee:
      options?.doctors?.map((doctor) => ({
        '@type': 'Person',
        '@id': getUrl(`/doctors/${doctor.doctorId}`, locale),
        name: doctor.name,
        jobTitle: doctor.chief ? 'Chief Medical Officer' : 'Medical Doctor',
        image: doctor.profileImageUrl || '',
        address: {
          '@type': 'PostalAddress',
          streetAddress: hospital.location.address,
          addressLocality: hospital.location.sido || '',
          addressCountry: hospital.country || 'KR',
        },
      })) || [],

    // 서비스 제공 목록
    hasOfferCatalog:
      options?.services && options.services.length > 0
        ? {
            '@type': 'OfferCatalog',
            name: 'Medical Services',
            itemListElement: options.services.map((service) => ({
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: service.title,
                description: `${hospital.name} - ${service.title}`,
              },
              price: service.price.discountedAmount,
              priceCurrency: service.price.currency,
            })),
          }
        : undefined,
  }
}

/**
 * 의사 스키마 생성 (Physician)
 */
export function createDoctorSchema(doctor: DoctorForUser, hospital: HospitalView, locale: Locales) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Physician',
    '@id': getUrl(`/doctors/${doctor.doctorId}`, locale),
    name: doctor.name,
    image: doctor.profileImageUrl || '',
    jobTitle: doctor.chief ? 'Chief Medical Officer' : 'Medical Doctor',

    // 전문 분야
    medicalSpecialty: doctor.specialist ? 'PlasticSurgery' : 'Dermatology',

    // 소속 병원
    worksFor: {
      '@type': ['LocalBusiness', 'MedicalBusiness'],
      '@id': getUrl(`/hospitals/${hospital.hospitalId}`, locale),
      name: hospital.name,
    },

    // 리뷰 통계
    aggregateRating:
      doctor.statistics.reviewCount > 0
        ? {
            '@type': 'AggregateRating',
            ratingCount: doctor.statistics.reviewCount,
          }
        : undefined,
  }
}

/**
 * 리뷰 스키마 생성
 */
export function createReviewSchema(
  review: {
    id?: string
    reviewBody: string
    totalRating?: number
    author: { nickName: string }
  },
  hospital: HospitalView,
  locale: Locales,
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Review',
    '@id': review.id ? getUrl(`/reviews/${review.id}`, locale) : undefined,
    reviewBody: review.reviewBody,

    // 평점 (있는 경우만)
    reviewRating: review.totalRating
      ? {
          '@type': 'Rating',
          ratingValue: review.totalRating,
          bestRating: 10,
        }
      : undefined,

    // 작성자
    author: {
      '@type': 'Person',
      name: review.author.nickName,
    },

    // 리뷰 대상 (병원)
    itemReviewed: {
      '@type': ['LocalBusiness', 'MedicalBusiness'],
      '@id': getUrl(`/hospitals/${hospital.hospitalId}`, locale),
      name: hospital.name,
    },

    // 발행자 (강남언니)
    publisher: {
      '@type': 'Organization',
      name: getPublisherName(locale),
      logo: {
        '@type': 'ImageObject',
        url: getPublisherLogo(locale),
      },
    },
  }
}

/**
 * 조직(강남언니) 스키마 생성
 */
export function createOrganizationSchema(locale: Locales) {
  const orgUrl = getUrl('/', locale)

  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${orgUrl}#organization`,
    name: getPublisherName(locale),
    url: orgUrl,
    logo: {
      '@type': 'ImageObject',
      url: getPublisherLogo(locale),
      width: 192,
      height: 192,
    },
    sameAs: getSocialMediaUrls(locale),
    foundingDate: '2015',
    description: getOrganizationDescription(locale),
  }
}

/**
 * 브레드크럼 스키마 생성
 */
export function createBreadcrumbSchema(breadcrumbs: { name: string; url: string }[], locale: Locales) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((breadcrumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: breadcrumb.name,
      item: breadcrumb.url,
    })),
  }
}

// Helper functions
function getSocialMediaUrls(locale: Locales): string[] {
  const baseUrls = [
    'https://play.google.com/store/apps/details?id=com.gangnam.sister',
    'https://itunes.apple.com/app/apple-store/id1035105027',
    'https://www.youtube.com/channel/UCm7h6S-9amJETdvSBgzCvSw',
  ]

  if (locale === Locales.JA_JP) {
    return [
      'https://www.instagram.com/gangnamunni_jp/',
      'https://twitter.com/gnun_jp',
      'https://line.me/R/ti/p/@434wqitx?oat__id=5001699',
      ...baseUrls,
    ]
  }

  return ['https://www.instagram.com/gangnamunni_kr/', 'https://www.facebook.com/gangnamunni.kr', ...baseUrls]
}

function getPublisherName(locale: Locales): string {
  const names = {
    [Locales.KO_KR]: '강남언니',
    [Locales.JA_JP]: 'カンナムオンニ',
    [Locales.EN_US]: 'Gangnam Unni',
    [Locales.TH_TH]: 'Gangnam Unni',
    [Locales.ZH_HANS_CN]: 'Gangnam Unni',
    [Locales.ZH_HANT_TW]: 'Gangnam Unni',
  }
  return names[locale] || names[Locales.KO_KR]
}

function getPublisherLogo(locale: Locales): string {
  const logos = {
    [Locales.KO_KR]: 'https://www.gangnamunni.com/images/kr/logo-kr.svg',
    [Locales.JA_JP]: 'https://www.gangnamunni.com/images/jp/logo.svg',
    [Locales.EN_US]: 'https://www.gangnamunni.com/images/en/logo.svg',
    [Locales.TH_TH]: 'https://www.gangnamunni.com/images/th/logo.svg',
    [Locales.ZH_HANS_CN]: 'https://www.gangnamunni.com/images/zh/logo.svg',
    [Locales.ZH_HANT_TW]: 'https://www.gangnamunni.com/images/zh/logo.svg',
  }
  return logos[locale] || logos[Locales.KO_KR]
}

function getOrganizationDescription(locale: Locales): string {
  const descriptions = {
    [Locales.KO_KR]: '1등 성형수술&피부시술 정보앱 강남언니',
    [Locales.JA_JP]: '日本最大級の美容医療・整形の口コミ予約アプリ',
    [Locales.EN_US]: "Korea's #1 plastic surgery & skin care information app",
    [Locales.TH_TH]: "Korea's #1 plastic surgery & skin care information app",
    [Locales.ZH_HANS_CN]: '韩国第一整形&皮肤科信息应用',
    [Locales.ZH_HANT_TW]: '韓國第一整形&皮膚科信息應用',
  }
  return descriptions[locale] || descriptions[Locales.KO_KR]
}
